import { useAuthStore } from "@/hooks/useAuthStore";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { isSubcontractorOnlyIn } from "@/lib/orgRoles";

/**
 * Is the viewer a project-scoped member of the current org — a subcontractor
 * with no owner/admin role? Such a member only ever sees the building shell's
 * Projects view: `useBuildingViewMode` and `useBuildingScope` both read the
 * effective mode as "projects" for them, whatever the stored mode says, and a
 * member with a single project is scoped to it without a pick.
 *
 * Reads the roles loaded with the session rather than `usePermissions`, so the
 * two scope hooks stay cheap and need no permission set to have loaded. It is
 * an affordance only: what a member can read is still their assignments' RLS.
 *
 * Must be used inside an `observer` component — it reads MST observables.
 */
export function useIsProjectScopedMember(): boolean {
  const auth = useAuthStore();
  const { currentOrganization } = useOrganizationContext();
  const orgId = currentOrganization?.id;
  if (!orgId) return false;
  return isSubcontractorOnlyIn((org, role) => auth.hasRoleInOrg(org, role), orgId);
}
